// Plain-language commands for the command bar and browser voice. Turns text
// like "build two benches near forge 1" into the same tool calls GPT-Live
// makes. Deliberately forgiving: speech recognizers mangle small words.

import { kindFromWords, normalize } from './commands';

export interface ParsedCommand {
  tool: string;
  args: Record<string, unknown>;
}

const COUNT_WORDS: Record<string, number> = {
  a: 1, an: 1, one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9, ten: 10, eleven: 11, twelve: 12,
  couple: 2, few: 3, pair: 2,
};

function splitList(s: string): string[] {
  return s.split(/\s*(?:,|\band\b|&)\s*/).map((x) => x.trim()).filter(Boolean);
}

/** Split a compound utterance ("build a forge then connect it to users") into parts. */
export function splitUtterance(text: string): string[] {
  return text
    .split(/\s*(?:[.;!?]+|\bthen\b|\band then\b|\bafter that\b)\s*/i)
    .map((t) => t.trim())
    .filter(Boolean);
}

export function parseCommand(raw: string): ParsedCommand | { help: string } | null {
  const text = raw.trim().toLowerCase().replace(/^(please|ok|okay|hey relay|relay|can you|could you|i want to|let's|lets)\s+/g, '').replace(/\s+please$/, '');
  if (!text) return null;
  let m: RegExpMatchArray | null;

  if (/^(help|commands|what can i say)\b/.test(text)) {
    return {
      help: 'Try: "build a forge near spec desk 1", "build two benches near forge 1", "connect forge 1 to bench 1", "send bench 1 rejects to postmortem 1", "connect bench 1 to users", "research better prompts", "rollback", "patch all", "reset breakers", "open the monolith", "ask to build in platform", "speed 2", "go to users", "undo".',
    };
  }
  if (/^(status|state|what's up|whats up|report)$/.test(text)) return { tool: 'get_state', args: {} };
  if (/^(undo|take that back|oops)$/.test(text)) return { tool: 'undo', args: {} };
  if (/^(test|try|simulate|check)( the| my)? plan$/.test(text)) return { tool: 'test_plan', args: {} };
  if (/^(submit|ship|lock in)( the| my)? plan$/.test(text)) return { tool: 'submit_plan', args: {} };
  if (/^(pause|stop|freeze|hold on)$/.test(text)) return { tool: 'set_speed', args: { speed: 0 } };
  if (/^(resume|play|go|unpause|continue)$/.test(text)) return { tool: 'set_speed', args: { speed: 1 } };
  if ((m = text.match(/^(?:speed|set speed(?: to)?|go)\s*(\d|one|two|four|zero)x?$/))) {
    const n = { zero: 0, one: 1, two: 2, four: 4 }[m[1] as 'one'] ?? Number(m[1]);
    return { tool: 'set_speed', args: { speed: n } };
  }
  if (/^(faster|speed up|fast forward)$/.test(text)) return { tool: 'set_speed', args: { speed: 4 } };
  if (/^(slower|slow down|normal speed)$/.test(text)) return { tool: 'set_speed', args: { speed: 1 } };
  if (/^(reset|clear) (the |all )?breakers?$/.test(text)) return { tool: 'reset_breakers', args: {} };
  if ((m = text.match(/^(?:roll ?back|restore|revert)(?: (?:at|to|from|using))?(?: (.+))?$/))) return { tool: 'rollback', args: m[1] ? { checkpoint: m[1] } : {} };
  if ((m = text.match(/^(?:patch|hotfix|fix|repair)(?: up)? (.+)$/))) return { tool: 'patch', args: { targets: /^(all|everything|it all)$/.test(m[1]) ? ['all'] : splitList(m[1]) } };
  if ((m = text.match(/^(?:research|learn|unlock|study) (.+)$/))) return { tool: 'research', args: { tech: m[1] } };
  if ((m = text.match(/^(?:go to|focus(?: on)?|show(?: me)?|zoom to|look at|jump to|center on) (.+)$/))) return { tool: 'focus', args: { target: m[1] } };
  if ((m = text.match(/^(?:claim|take|grab|log ?in(?: to)?|login(?: to)?|use) (.+)$/))) return { tool: 'claim', args: { target: m[1] } };
  if ((m = text.match(/^(?:stamp|paste|place blueprint|stamp blueprint)(?: (?:near|at|by) (.+))?$/))) return { tool: 'stamp_blueprint', args: { near: m[1] ?? 'cursor' } };

  // Permission. "ask to open the monolith", "just do it", "open the monolith", "build in platform anyway"
  if ((m = text.match(/^(?:ask|request|get)(?: for)?(?: permission)?(?: (?:to|for))?(?: (?:open|tap|build in|use|enter))? (.+)$/))) return { tool: 'ask_permission', args: { what: m[1] } };
  if ((m = text.match(/^(?:open|break open|crack open|tap|smash|break into|take over|claim zone|take zone) (.+)$/))) return { tool: 'act_without_permission', args: { what: m[1] } };
  if ((m = text.match(/^(?:act|just do it|do it anyway|go ahead|build in) (.+?)(?: anyway| without asking| without permission)?$/))) return { tool: 'act_without_permission', args: { what: m[1] } };

  // "send bench 1 rejects to postmortem 1", "route bugs from bench 2 to postmortem"
  if ((m = text.match(/^(?:send|route|connect|wire|pipe|hook up) (.+?)(?:'s)? (?:rejects?|bugs?|red side) (?:to|into|->) (.+)$/))) return { tool: 'connect', args: { from: m[1], to: m[2], port: 'rejects' } };
  if ((m = text.match(/^(?:send|route|connect|wire|pipe|hook up) (?:rejects?|bugs?) (?:of|from) (.+?) (?:to|into|->) (.+)$/))) return { tool: 'connect', args: { from: m[1], to: m[2], port: 'rejects' } };
  if ((m = text.match(/^(?:connect|wire|link|belt|hook up|feed|route|send) (.+?) (?:to|into|->|with|and) (.+)$/))) return { tool: 'connect', args: { from: m[1], to: m[2] } };

  if ((m = text.match(/^(?:remove|delete|destroy|scrap|demolish|tear down) (.+)$/))) return { tool: 'remove', args: { targets: splitList(m[1]) } };
  if ((m = text.match(/^(?:rotate|turn|face|point) (.+?) (?:to |towards? )?(east|south|west|north|left|right|up|down)$/))) return { tool: 'rotate', args: { target: m[1], facing: m[2] } };

  // "build two benches near forge 1 facing east"
  if ((m = text.match(/^(?:build|place|add|make|put|drop|spawn|create)\s+(.+)$/))) {
    let rest = m[1];
    let facing: string | undefined;
    let near: string | undefined;
    let resource: string | undefined;
    const f = rest.match(/\s+(?:facing|pointing|pointed|aimed)\s+(east|south|west|north|left|right|up|down)$/);
    if (f) {
      facing = f[1];
      rest = rest.slice(0, f.index);
    }
    const on = rest.match(/\s+on\s+(?:the\s+)?(request|requests|context)(?:\s+ground)?$/);
    if (on) {
      resource = on[1].startsWith('request') ? 'request' : 'context';
      rest = rest.slice(0, on.index);
    }
    const nr = rest.match(/\s+(?:near|next to|beside|by|at|around|close to|in front of|behind)\s+(.+)$/);
    if (nr) {
      near = nr[1];
      rest = rest.slice(0, nr.index);
    }
    const parts = rest.trim().split(/\s+/);
    let count = 1;
    if (parts.length > 1) {
      if (/^\d+$/.test(parts[0])) count = Number(parts.shift());
      else if (COUNT_WORDS[parts[0]]) count = COUNT_WORDS[parts.shift()!];
    }
    const kindText = parts.join(' ');
    const kind = kindFromWords(kindText) ?? kindFromWords(normalize(kindText));
    if (!kind) return null;
    if (kind === 'indexer' && !resource && near && /^(request|requests|context)$/.test(near)) {
      resource = near.startsWith('request') ? 'request' : 'context';
    }
    return { tool: 'build', args: { kind, count, near, facing, resource } };
  }
  return null;
}
